// FeatureLockWall.tsx — shown when a lapsed-trial (non-member) user opens a locked
// premium feature (guided meditations, most toolkit exercises). Members and trial
// users never see it. The grounding/safety tools, roll-the-dice and the panic
// route stay free, so they never reach here. Purchase happens on the website; the
// button opens checkout with the founder coupon while the window is open.
import { Btn, Display, Body } from '@/components/ui';
import { Icon } from '@/components/icons';
import { MembershipRefresh } from '@/components/MembershipRefresh';
import { PURCHASE_URL, SHOW_PURCHASE, SHOW_IAP } from '@/config/supabase';
import { useMembershipRecheckOnResume } from '@/hooks/useMembershipRecheckOnResume';
import { openPaywall } from '@/services/paywallStore';
import { TRIAL_FULL_LOCK } from '@/config/features';

const STAYS_FREE = TRIAL_FULL_LOCK
  ? 'Feeling unsafe support and the Self-compassion course are always free.'
  : 'Grounding for panic, and the self-compassion course, stay free.';
// What membership opens, as the user meets it (TRIAL_FULL_LOCK locks all of these after the trial).
const COVERS_INVITE = TRIAL_FULL_LOCK
  ? 'Membership opens the full Recovery programme, calming tools, CARE, the Toolkit, the Library and Guide me, whenever you need them.'
  : 'Membership opens the meditations and tools whenever you need them.';

// trialActive: in-trial users can meet this too (e.g. Guide me is members-only), so don't tell them
// their trial is over.
export function FeatureLockWall({ onRefresh, onClose, trialActive = false }: { onRefresh: () => Promise<boolean>; onClose: () => void; trialActive?: boolean }) {
  useMembershipRecheckOnResume(true, onClose);
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 118,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 'calc(var(--safe-top) + 32px) 30px calc(var(--safe-bottom) + 26px)',
      }}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'absolute', top: 'calc(var(--safe-top) + 12px)', right: 16,
          background: 'none', border: 'none', color: 'var(--ink-soft)',
          minHeight: 44, minWidth: 44, cursor: 'pointer',
        }}
      >
        <Icon name="chevDown" size={22} />
      </button>

      <div
        style={{
          width: 80, height: 80, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#FBF1DE', background: 'linear-gradient(150deg, #E1A943, #9B6712)', marginBottom: 20,
        }}
      >
        <Icon name="sparkle" size={34} />
      </div>
      {trialActive
        ? <Display parts={{ pre: 'Unlock ', em: 'everything', post: '.' }} size={26} lh={1.18} />
        : <Display parts={{ pre: 'Keep going with ', em: 'ReNurture', post: '.' }} size={26} lh={1.18} />}
      <Body size={15.5} style={{ margin: '16px auto 0', maxWidth: 330, lineHeight: 1.6 }}>
        {(trialActive
          ? 'This is part of membership. Join to unlock it, along with everything else in ReNurture. '
          : 'Your 10 free days are up, and we’d love you to keep going. ' + COVERS_INVITE + ' ') +
          (SHOW_PURCHASE
            ? 'Join on our website, then sign back in here and you’ll pick up exactly where you left off.'
            : SHOW_IAP
            ? 'Your progress is saved, so you’ll pick up exactly where you left off.'
            : 'Membership is available on the ReNurture website. Once you’ve joined, sign in here and you’ll pick up exactly where you left off.')}
      </Body>
      {!trialActive && (
        <Body dim size={13.5} style={{ margin: '12px auto 0', maxWidth: 320, lineHeight: 1.5 }}>{STAYS_FREE}</Body>
      )}
      <div style={{ marginTop: 24, width: '100%', maxWidth: 320 }}>
        {SHOW_IAP && (
          <Btn full size="lg" onClick={openPaywall}>
            Become a member
          </Btn>
        )}
        {SHOW_PURCHASE && PURCHASE_URL && (
          <Btn full size="lg" onClick={() => window.open(PURCHASE_URL, '_blank', 'noopener')} leading={<Icon name="external" size={18} />}>
            Become a member
          </Btn>
        )}
        <MembershipRefresh onRefresh={onRefresh} onUnlocked={onClose} />
      </div>
    </div>
  );
}
